import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CelebrationIcon from '@mui/icons-material/Celebration';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { RootState } from '../../store';
import { useAdminObterOnboardingQuery, useAdminResumoIdentificadoresQuery } from '../../api';
import { montarUrlIdentificador } from '../../lib/url-identificador';
import { onAbrirOnboarding } from '../../lib/onboarding-eventos';

/** Passos do checklist (metadata de apresentação no front; o backend só diz o que está feito). */
interface PassoDef {
  chave: 'identidade' | 'horarios' | 'pagamento' | 'entrega';
  label: string;
  descricao: string;
  rota: string;
}

const PASSOS: PassoDef[] = [
  { chave: 'identidade', label: 'Identidade da loja', descricao: 'Envie a logo e a capa da sua loja', rota: '/admin/configuracao' },
  { chave: 'horarios', label: 'Horários de funcionamento', descricao: 'Defina quando a loja aceita pedidos', rota: '/admin/horarios' },
  { chave: 'pagamento', label: 'Formas de pagamento', descricao: 'Escolha como o cliente vai pagar', rota: '/admin/formas-pagamento' },
  { chave: 'entrega', label: 'Taxa de entrega', descricao: 'Configure o valor da entrega (por bairro ou km)', rota: '/admin/taxa-entrega' },
];

/**
 * Dialog "Primeiros passos" (onboarding), aberto pelo botão do menu lateral e
 * pelo aviso na tela de Configuração (evento `abrirOnboarding`). Também abre
 * sozinho 1x por sessão quando a loja está criada mas incompleta (< 100%).
 * Fica montado no AdminLayout.
 */
export function OnboardingDialog(): JSX.Element | null {
  const navigate = useNavigate();
  const idConfig = useSelector((s: RootState) => s.adminAuth.idConfiguracaoSelecionada);
  const { data: onboarding } = useAdminObterOnboardingQuery(idConfig as number, {
    skip: !idConfig,
    refetchOnMountOrArgChange: true,
  });
  const { data: resumoId } = useAdminResumoIdentificadoresQuery(idConfig as number, { skip: !idConfig });
  const [aberto, setAberto] = useState(false);

  // Abertura via evento (menu / aviso da configuração).
  useEffect(() => onAbrirOnboarding(() => setAberto(true)), []);

  // Abre sozinho 1x por sessão quando a loja está criada mas ainda incompleta.
  useEffect(() => {
    if (!idConfig || !onboarding || onboarding.percentual >= 100) return;
    const chave = `onboarding-auto-${idConfig}`;
    if (sessionStorage.getItem(chave)) return;
    sessionStorage.setItem(chave, '1');
    setAberto(true);
  }, [idConfig, onboarding]);

  if (!idConfig) return null;

  const deliveryIdent = resumoId?.resumo.DELIVERY?.itens?.[0];
  const linkLoja = deliveryIdent
    ? montarUrlIdentificador({ tipo: 'DELIVERY', codigo: deliveryIdent.codigo, dominioCompleto: deliveryIdent.dominioCompleto })
    : null;

  const passos = onboarding ? PASSOS.filter((p) => p.chave !== 'entrega' || onboarding.fazDelivery) : [];
  const completo = (onboarding?.percentual ?? 0) >= 100;

  function irPara(rota: string): void {
    setAberto(false);
    navigate(rota);
  }

  return (
    <Dialog open={aberto} onClose={() => setAberto(false)} fullWidth maxWidth="sm">
      <DialogTitle>Primeiros passos</DialogTitle>
      <DialogContent dividers>
        {!onboarding ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : completo ? (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CelebrationIcon color="success" fontSize="large" />
            <Box>
              <Typography variant="h6">Sua loja está pronta! 🎉</Typography>
              <Typography variant="body2" color="text.secondary">
                Tudo configurado. Você pode ajustar qualquer item pelo menu quando quiser.
              </Typography>
            </Box>
          </Stack>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Complete a configuração para começar a receber pedidos.
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={onboarding.percentual}
                sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                {onboarding.percentual}%
              </Typography>
            </Stack>
            <List disablePadding>
              {passos.map((p) => {
                const feito = onboarding[p.chave] === true;
                return (
                  <ListItem
                    key={p.chave}
                    disableGutters
                    secondaryAction={
                      feito ? undefined : (
                        <Button size="small" variant="contained" onClick={() => irPara(p.rota)}>
                          Configurar
                        </Button>
                      )
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {feito ? <CheckCircleIcon color="success" /> : <RadioButtonUncheckedIcon color="disabled" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={p.label}
                      secondary={feito ? 'Concluído' : p.descricao}
                      primaryTypographyProps={{ fontWeight: feito ? 400 : 600 }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {linkLoja && (
          <Button startIcon={<OpenInNewIcon />} href={linkLoja} target="_blank" rel="noopener">
            Ver minha loja
          </Button>
        )}
        <Button onClick={() => setAberto(false)}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
